import { z } from "zod";
import { moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { fuelPaymentTypeSchema, ingestTankReadingSourceSchema, ingestTotalizerSourceSchema, millimetreSchema, temperatureSchema, } from "./readings.js";
/**
 * Toplu veri alımı (ingestion) — istasyon ajanı, otomasyon ya da CSV aktarımı aynı uçları kullanır (Prompt 12 §1;
 * ajan kimliği Prompt 14).
 *
 * Sözleşmenin üç değişmezi:
 *  1. **Parti düşmez.** Tek kaydın hatası diğerlerini yazmayı engellemez; hatalar `rejected[]` içinde `index` ile
 *     raporlanır. Bu yüzden Zod doğrulaması KAYIT KAYIT yapılır; yalnızca ZARF (istasyon, dizi, üst sınır) tek
 *     seferde doğrulanır ve hatası 400 döner.
 *  2. **Idempotency KAYIT düzeyindedir.** Doğal anahtar çakışması (`ON CONFLICT DO NOTHING`) hata değil
 *     `duplicates`'tir; aynı parti iki kez gönderilince ikinci yanıt `accepted: 0` olur ve özetler değişmez.
 *     `Idempotency-Key` başlığı isteğe bağlıdır (yalnızca aynı HTTP yanıtını tekrarlar).
 *  3. **Türetilen alanlar SUNUCUDA hesaplanır:** hacim kalibrasyon tablosundan, net hacim sıcaklık düzeltmesiyle,
 *     tank / ürün tabancadan, iş günü `occurredAt`'in Europe/Istanbul takvim gününden.
 */
/** Bir partideki en çok kayıt sayısı. Aşılırsa parti hiç işlenmez (400). */
export const FUEL_INGEST_MAX_ITEMS = 5000;
/** Gelecek zaman toleransı: istasyon saati bu kadar ileri gidebilir (sn). Üstü reddedilir. */
export const FUEL_INGEST_FUTURE_TOLERANCE_SECONDS = 300;
/** Pompa yuvarlamasına izin verilen tutar sapması (kuruş): |tutar − round(litre × fiyat)|. */
export const FUEL_AMOUNT_TOLERANCE_KURUS = 2;
export const FUEL_INGEST_REJECTION_CODES = [
    "INVALID_RECORD",
    "UNKNOWN_TANK",
    "UNKNOWN_NOZZLE",
    "FOREIGN_STATION",
    "INACTIVE_ASSET",
    "STRAPPING_TABLE_MISSING",
    "LEVEL_OUT_OF_RANGE",
    "FUTURE_TIMESTAMP",
    "NON_POSITIVE_QUANTITY",
    "AMOUNT_MISMATCH",
    "TEST_PAYMENT_TYPE",
];
export const fuelIngestRejectionCodeSchema = z.enum(FUEL_INGEST_REJECTION_CODES);
export const FUEL_INGEST_REJECTION_LABELS = {
    INVALID_RECORD: "Kayıt biçimi geçersiz",
    UNKNOWN_TANK: "Tank bulunamadı",
    UNKNOWN_NOZZLE: "Tabanca bulunamadı",
    FOREIGN_STATION: "Varlık başka bir istasyona ait",
    INACTIVE_ASSET: "Varlık hizmet dışı",
    STRAPPING_TABLE_MISSING: "Tankın geçerli kalibrasyon tablosu yok",
    LEVEL_OUT_OF_RANGE: "Seviye kalibrasyon tablosunun aralığı dışında",
    FUTURE_TIMESTAMP: "Zaman damgası gelecekte",
    NON_POSITIVE_QUANTITY: "Miktar sıfır ya da negatif",
    AMOUNT_MISMATCH: "Tutar, litre × birim fiyat ile uyuşmuyor",
    TEST_PAYMENT_TYPE: "Test çekişinde ödeme türü NONE olmalıdır",
};
// ── Kayıt şemaları ───────────────────────────────────────────────────────────
export const ingestTankReadingSchema = z.object({
    tankId: uuidSchema,
    measuredAt: timestampSchema,
    levelMm: millimetreSchema,
    waterMm: millimetreSchema.nullable().optional(),
    temperatureC: temperatureSchema.nullable().optional(),
    source: ingestTankReadingSourceSchema,
});
export const ingestTransactionSchema = z.object({
    nozzleId: uuidSchema,
    occurredAt: timestampSchema.describe("Dolumun BİTTİĞİ an"),
    litres: quantitySchema,
    unitPrice: unitPriceSchema.describe("KDV dâhil ₺/L"),
    amount: moneySchema.describe("Kuruş, KDV dâhil"),
    paymentType: fuelPaymentTypeSchema,
    agentTxId: z
        .string()
        .min(1)
        .max(100)
        .describe("Ajan / otomasyon işlem kimliği — idempotency doğal anahtarı"),
    isTest: z.boolean().optional(),
    testReturnedToTank: z.boolean().optional(),
    attendantUserId: uuidSchema.nullable().optional(),
    vehiclePlate: z.string().max(20).nullable().optional().describe("KVKK: kişisel veri; günlüğe yazılmaz"),
    vehicleTagId: z.string().max(64).nullable().optional(),
    counterpartyId: uuidSchema.nullable().optional(),
    fiscalReceiptRef: z.string().max(64).nullable().optional(),
    totalizerStart: quantitySchema.nullable().optional(),
    totalizerEnd: quantitySchema.nullable().optional(),
});
export const ingestTotalizerReadingSchema = z.object({
    nozzleId: uuidSchema,
    readAt: timestampSchema,
    electronicL: quantitySchema,
    mechanicalL: quantitySchema.nullable().optional(),
    source: ingestTotalizerSourceSchema,
});
// ── Zarf ─────────────────────────────────────────────────────────────────────
const envelopeOf = (item) => z.object({
    stationId: uuidSchema,
    items: z.array(item).min(1).max(FUEL_INGEST_MAX_ITEMS),
});
export const ingestTankReadingsBodySchema = envelopeOf(ingestTankReadingSchema);
export const ingestTransactionsBodySchema = envelopeOf(ingestTransactionSchema);
export const ingestTotalizersBodySchema = envelopeOf(ingestTotalizerReadingSchema);
/**
 * Sunucunun ZARFI doğrularken kullandığı şema: kayıtlar burada `unknown` bırakılır, çünkü her kayıt AYRI AYRI
 * doğrulanır (tek bozuk kayıt partiyi düşürmez). Belgelenen (OpenAPI) gövde şemaları yukarıdakilerdir.
 */
export const fuelIngestEnvelopeSchema = z.object({
    stationId: uuidSchema,
    items: z.array(z.unknown()).min(1).max(FUEL_INGEST_MAX_ITEMS),
});
// ── Yanıt ────────────────────────────────────────────────────────────────────
export const fuelIngestRejectionSchema = z.object({
    index: z.number().int().nonnegative().describe("İstekteki `items` dizisindeki sıra"),
    code: fuelIngestRejectionCodeSchema,
    message: z.string(),
});
/**
 * Kayıt YAZILDI ama dikkat çeken bir şey var. Red DEĞİLDİR: ham ölçüm hiçbir zaman reddedilmez, yalnızca
 * türetilmiş değerlere (tabancanın anlık endeksi) katılmayabilir.
 */
export const FUEL_INGEST_WARNING_CODES = [
    "TOTALIZER_ANOMALY",
    "TOTALIZER_ROLLOVER",
    "MECHANICAL_MISMATCH",
    "STALE_PRICE_SALE",
];
export const fuelIngestWarningCodeSchema = z.enum(FUEL_INGEST_WARNING_CODES);
export const FUEL_INGEST_WARNING_LABELS = {
    TOTALIZER_ANOMALY: "Endeks süreklilik anomalisi (okuma yazıldı, endeks ilerletilmedi)",
    TOTALIZER_ROLLOVER: "Endeks sayacı taştı (satış sınır üzerinden hesaplandı)",
    MECHANICAL_MISMATCH: "Elektronik ↔ mekanik endeks artışı eşiği aştı",
    STALE_PRICE_SALE: "Yürürlükteki fiyattan değil, eski fiyattan satış",
};
export const fuelIngestWarningSchema = z.object({
    index: z.number().int().nonnegative().describe("İstekteki `items` dizisindeki sıra"),
    code: fuelIngestWarningCodeSchema,
    message: z.string(),
});
export const fuelIngestResultSchema = z.object({
    stationId: uuidSchema,
    received: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative().describe("Yeni yazılan kayıt sayısı"),
    duplicates: z.number().int().nonnegative().describe("Doğal anahtarı zaten var olan kayıtlar (hata DEĞİL)"),
    rejected: z.array(fuelIngestRejectionSchema),
    /** Geriye uyumlu: eski istemciler için varsayılan boş dizi. */
    warnings: z.array(fuelIngestWarningSchema).default([]),
});
//# sourceMappingURL=ingest.js.map